import { useEffect, useState } from 'react';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import MediaPickerDialog from '@/client/components/cms/MediaPickerDialog';
import '@/client/components/cms/editor.css';

/** What a send needs: the HTML part and a plain-text part for clients that want it. */
export interface EmailContent {
  html: string;
  text: string;
}

const isEmpty = (editor: Editor) => editor.isEmpty || !editor.getText().trim();

const setLink = (editor: Editor) => {
  const previous = editor.getAttributes('link').href as string | undefined;
  const url = window.prompt('Link address', previous || 'https://');
  if (url === null) return;
  if (!url.trim()) {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
};

const BLOCKS = [
  { id: 'p', label: 'Normal text' },
  { id: '1', label: 'Heading 1' },
  { id: '2', label: 'Heading 2' },
  { id: '3', label: 'Heading 3' },
] as const;

const blockOf = (editor: Editor) =>
  ([1, 2, 3] as const).find((level) => editor.isActive('heading', { level }))?.toString() ?? 'p';

/**
 * Rich-text body for an email, with a fixed toolbar like a mail client.
 *
 * Only what survives in email clients: formatting is inline styles and plain
 * tags, images are hosted (picked from the CMS media library), no embeds.
 * `value` is read when it changes from outside (a template loaded, the form
 * cleared), not on every keystroke. `editable={false}` renders a read-only view.
 */
export default function EmailEditor({
  value,
  onChange,
  placeholder = 'Write your message...',
  minHeight = 200,
  editable = true,
}: {
  value: string;
  onChange?: (content: EmailContent) => void;
  placeholder?: string;
  minHeight?: number;
  editable?: boolean;
}) {
  const [pickingImage, setPickingImage] = useState(false);
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ HTMLAttributes: { style: 'max-width:100%;height:auto;' } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    editorProps: {
      attributes: { class: 'cms-tt-editor focus:outline-none px-4 py-3', style: `min-height:${minHeight}px` },
    },
    onUpdate: ({ editor: e }) =>
      onChange?.(isEmpty(e) ? { html: '', text: '' } : { html: e.getHTML(), text: e.getText({ blockSeparator: '\n\n' }) }),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === '' && isEmpty(editor))) {
      editor.commands.setContent(value, true);
    }
  }, [editor, value]);

  if (!editor) return null;

  if (!editable) {
    return <EditorContent editor={editor} />;
  }

  const chain = () => editor.chain().focus();
  const tools: { label: string; icon: typeof Bold; run: () => void; active?: boolean; disabled?: boolean }[][] = [
    [
      { label: 'Bold', icon: Bold, run: () => chain().toggleBold().run(), active: editor.isActive('bold') },
      { label: 'Italic', icon: Italic, run: () => chain().toggleItalic().run(), active: editor.isActive('italic') },
      { label: 'Underline', icon: UnderlineIcon, run: () => chain().toggleUnderline().run(), active: editor.isActive('underline') },
      { label: 'Strikethrough', icon: Strikethrough, run: () => chain().toggleStrike().run(), active: editor.isActive('strike') },
    ],
    [
      { label: 'Align left', icon: AlignLeft, run: () => chain().setTextAlign('left').run(), active: editor.isActive({ textAlign: 'left' }) },
      { label: 'Align centre', icon: AlignCenter, run: () => chain().setTextAlign('center').run(), active: editor.isActive({ textAlign: 'center' }) },
      { label: 'Align right', icon: AlignRight, run: () => chain().setTextAlign('right').run(), active: editor.isActive({ textAlign: 'right' }) },
    ],
    [
      { label: 'Bulleted list', icon: List, run: () => chain().toggleBulletList().run(), active: editor.isActive('bulletList') },
      { label: 'Numbered list', icon: ListOrdered, run: () => chain().toggleOrderedList().run(), active: editor.isActive('orderedList') },
      { label: 'Quote', icon: Quote, run: () => chain().toggleBlockquote().run(), active: editor.isActive('blockquote') },
    ],
    [
      { label: 'Link', icon: LinkIcon, run: () => setLink(editor), active: editor.isActive('link') },
      { label: 'Image', icon: ImageIcon, run: () => setPickingImage(true) },
      { label: 'Divider', icon: Minus, run: () => chain().setHorizontalRule().run() },
      { label: 'Clear formatting', icon: RemoveFormatting, run: () => chain().unsetAllMarks().clearNodes().unsetTextAlign().run() },
    ],
    [
      { label: 'Undo', icon: Undo2, run: () => chain().undo().run(), disabled: !editor.can().undo() },
      { label: 'Redo', icon: Redo2, run: () => chain().redo().run(), disabled: !editor.can().redo() },
    ],
  ];

  const setBlock = (id: string) => {
    if (id === 'p') chain().setParagraph().run();
    else chain().setHeading({ level: Number(id) as 1 | 2 | 3 }).run();
  };

  const color = (editor.getAttributes('textStyle').color as string | undefined) || '#111827';

  return (
    <div className="rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 px-2 py-1.5" role="toolbar" aria-label="Formatting">
        <select
          aria-label="Text style"
          value={blockOf(editor)}
          onChange={(e) => setBlock(e.target.value)}
          className="h-8 rounded border border-gray-200 bg-white px-1.5 text-xs text-gray-700"
        >
          {BLOCKS.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
        <label title="Text colour" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-gray-100">
          <span className="sr-only">Text colour</span>
          <input
            type="color"
            value={color}
            onChange={(e) => chain().setColor(e.target.value).run()}
            className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0"
          />
        </label>
        {tools.map((group, i) => (
          <div key={i} className="flex items-center gap-0.5 border-l border-gray-200 pl-1">
            {group.map(({ label, icon: Icon, run, active, disabled }) => (
              <button
                key={label}
                type="button"
                title={label}
                aria-label={label}
                aria-pressed={active}
                disabled={disabled}
                onClick={run}
                className={`rounded p-1.5 ${active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'} disabled:opacity-30`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        ))}
      </div>
      <EditorContent editor={editor} />
      <MediaPickerDialog
        open={pickingImage}
        onClose={() => setPickingImage(false)}
        onSelect={(asset) => {
          setPickingImage(false);
          if (asset.public_url) chain().setImage({ src: asset.public_url, alt: asset.alt_text ?? '' }).run();
        }}
      />
    </div>
  );
}
